import {
  useCreateBillingEntityAdminMutation,
  useCreateBillingEntityMutation,
  useDeleteBillingEntityAdminMutation,
  useDeleteBillingEntityMutation,
  useGetBillingEntitiesAdminQuery,
  useGetBillingEntitiesQuery,
  useGetBillingEntityAdminQuery,
  useGetBillingEntityQuery,
  useSetDefaultBillingEntityMutation,
  useSyncBillingEntityAdminMutation,
  useSyncBillingEntityMutation,
  useUpdateBillingEntityAdminMutation,
  useUpdateBillingEntityMutation,
} from './api';

/**
 * Hook to get billing entities for the current provider's organization
 */
export function useBillingEntities(options?: { skip?: boolean }) {
  const result = useGetBillingEntitiesQuery(undefined, {
    skip: options?.skip,
  });

  return {
    entities: result.data?.billing_entities || [],
    isLoading: result.isLoading,
    isError: result.isError,
    isFetching: result.isFetching,
    error: result.error,
    refetch: result.refetch,
  };
}

/**
 * Hook to get billing entities for an organization (admin use)
 */
export function useBillingEntitiesAdmin(
  organizationId: string | null,
  options?: { skip?: boolean },
) {
  const result = useGetBillingEntitiesAdminQuery(
    { organizationId: organizationId! },
    {
      skip: !organizationId || options?.skip,
    },
  );

  return {
    entities: result.data?.billing_entities || [],
    isLoading: result.isLoading,
    isError: result.isError,
    isFetching: result.isFetching,
    error: result.error,
    refetch: result.refetch,
  };
}

/**
 * Hook to get a single billing entity (provider)
 */
export function useBillingEntity(
  billingEntityId: string | null,
  options?: { skip?: boolean },
) {
  const result = useGetBillingEntityQuery(
    { billingEntityId: billingEntityId! },
    {
      skip: !billingEntityId || options?.skip,
    },
  );

  return {
    entity: result.data || null,
    isLoading: result.isLoading,
    isError: result.isError,
    isFetching: result.isFetching,
    error: result.error,
    refetch: result.refetch,
  };
}

/**
 * Hook to get a single billing entity (admin)
 */
export function useBillingEntityAdmin(
  organizationId: string | null,
  billingEntityId: string | null,
  options?: { skip?: boolean },
) {
  const result = useGetBillingEntityAdminQuery(
    {
      organizationId: organizationId!,
      billingEntityId: billingEntityId!,
    },
    {
      skip: !organizationId || !billingEntityId || options?.skip,
    },
  );

  return {
    entity: result.data || null,
    isLoading: result.isLoading,
    isError: result.isError,
    isFetching: result.isFetching,
    error: result.error,
    refetch: result.refetch,
  };
}

/**
 * Hook for billing entity CRUD operations (provider)
 */
export function useBillingEntityOperations() {
  const [create, createResult] = useCreateBillingEntityMutation();
  const [update, updateResult] = useUpdateBillingEntityMutation();
  const [remove, removeResult] = useDeleteBillingEntityMutation();
  const [sync, syncResult] = useSyncBillingEntityMutation();
  const [setDefault, setDefaultResult] = useSetDefaultBillingEntityMutation();

  return {
    // Create
    create,
    createLoading: createResult.isLoading,
    createError: createResult.error,
    createSuccess: createResult.isSuccess,

    // Update
    update,
    updateLoading: updateResult.isLoading,
    updateError: updateResult.error,
    updateSuccess: updateResult.isSuccess,

    // Delete
    remove,
    removeLoading: removeResult.isLoading,
    removeError: removeResult.error,
    removeSuccess: removeResult.isSuccess,

    // Sync to Stedi
    sync,
    syncLoading: syncResult.isLoading,
    syncError: syncResult.error,
    syncSuccess: syncResult.isSuccess,

    // Set Default
    setDefault,
    setDefaultLoading: setDefaultResult.isLoading,
    setDefaultError: setDefaultResult.error,
    setDefaultSuccess: setDefaultResult.isSuccess,
  };
}

/**
 * Hook for billing entity CRUD operations (admin)
 */
export function useBillingEntityOperationsAdmin() {
  const [create, createResult] = useCreateBillingEntityAdminMutation();
  const [update, updateResult] = useUpdateBillingEntityAdminMutation();
  const [remove, removeResult] = useDeleteBillingEntityAdminMutation();
  const [sync, syncResult] = useSyncBillingEntityAdminMutation();

  return {
    // Create
    create,
    createLoading: createResult.isLoading,
    createError: createResult.error,
    createSuccess: createResult.isSuccess,

    // Update
    update,
    updateLoading: updateResult.isLoading,
    updateError: updateResult.error,
    updateSuccess: updateResult.isSuccess,

    // Delete
    remove,
    removeLoading: removeResult.isLoading,
    removeError: removeResult.error,
    removeSuccess: removeResult.isSuccess,

    // Sync to Stedi
    sync,
    syncLoading: syncResult.isLoading,
    syncError: syncResult.error,
    syncSuccess: syncResult.isSuccess,
  };
}
